import { api } from "../lib/api";
import { runtimeEnvironment } from "../lib/runtimeEnvironment";

export interface ClientShortlist {
  id: string;
  client_id: string;
  owner_user_id: string;
  public_token: string;
  status: "active" | "archived" | "revoked";
  title?: string | null;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
  revoked_at?: string | null;
}

export interface ClientShortlistItem {
  id: string;
  shortlist_id: string;
  property_id: string;
  sort_order: number;
  status: "active" | "removed";
  operator_note?: string | null;
  metadata?: Record<string, unknown>;
  added_at?: string;
  removed_at?: string | null;
}

export interface ClientShortlistProperty {
  id: string;
  name: string;
  address?: string | null;
  city?: string | null;
  neighborhood?: string | null;
  min_rent?: number | null;
  max_rent?: number | null;
  pets_allowed?: boolean | null;
  available_floor_plans?: number | null;
  total_floor_plans?: number | null;
  s3_image_url?: string | null;
  specials_summary?: string | null;
  specials?: string | null;
  updated_at?: string | null;
}

export interface ClientShortlistEvent {
  id: string;
  shortlist_id: string;
  item_id?: string | null;
  event_type: string;
  channel: string;
  created_at: string;
  metadata?: Record<string, unknown>;
}

export interface ClientShortlistDetail {
  shortlist: ClientShortlist;
  items: ClientShortlistItem[];
  properties: ClientShortlistProperty[];
  events: ClientShortlistEvent[];
}

interface ClientShortlistListResponse {
  shortlists: ClientShortlist[];
}

export function buildPublicShortlistUrl(publicToken: string): string {
  return `${runtimeEnvironment.publicAppUrl}/s/${publicToken}`;
}

export async function getOrCreateClientShortlist(clientId: string): Promise<ClientShortlist> {
  return api.post<ClientShortlist>("/shortlists/get-or-create", {
    client_id: clientId,
    title: "Client shortlist",
  });
}

export async function listClientShortlists(clientId: string): Promise<ClientShortlist[]> {
  const params = new URLSearchParams({
    client_id: clientId,
    status: "active",
    limit: "10",
  });
  const response = await api.get<ClientShortlistListResponse>(`/shortlists?${params.toString()}`);
  return response.shortlists || [];
}

export async function getClientShortlistDetail(shortlistId: string): Promise<ClientShortlistDetail> {
  return api.get<ClientShortlistDetail>(`/shortlists/${shortlistId}`);
}

export async function addClientShortlistItem(
  shortlistId: string,
  propertyId: string,
  sortOrder: number,
  metadata?: Record<string, unknown>
): Promise<ClientShortlistItem> {
  return api.post<ClientShortlistItem>(`/shortlists/${shortlistId}/items`, {
    property_id: propertyId,
    sort_order: sortOrder,
    ...(metadata ? { metadata } : {}),
  });
}

export async function removeClientShortlistItem(
  shortlistId: string,
  itemId: string
): Promise<ClientShortlistItem> {
  return api.delete<ClientShortlistItem>(`/shortlists/${shortlistId}/items/${itemId}`);
}

export async function updateClientShortlistItem(
  shortlistId: string,
  itemId: string,
  updates: {
    operator_note?: string | null;
    sort_order?: number;
    status?: string;
    metadata?: Record<string, unknown>;
  }
): Promise<ClientShortlistItem> {
  return api.patch<ClientShortlistItem>(
    `/shortlists/${shortlistId}/items/${itemId}`,
    updates
  );
}

export async function reorderClientShortlistItems(
  shortlistId: string,
  orderedItemIds: string[]
): Promise<ClientShortlistItem[]> {
  const response = await api.post<{ items: ClientShortlistItem[] }>(
    `/shortlists/${shortlistId}/items/reorder`,
    { ordered_item_ids: orderedItemIds }
  );
  return response.items || [];
}

/**
 * One share ACTION = one idempotency key. A fresh crypto.randomUUID() per call
 * gave the backend's unique-key dedup nothing to catch — a double-tap produced
 * two distinct keys and therefore two share events. Within the reuse window,
 * repeat calls for the same shortlist resend the SAME key (the backend treats
 * the resulting unique violation as "already recorded" and returns the existing
 * row) and the same client_sequence. A new action after the window mints a new
 * key and increments the sequence, so the ledger still counts deliberate
 * re-shares.
 */
const SHARE_ACTION_REUSE_MS = 60_000;
const shareActionByShortlist = new Map<
  string,
  { key: string; sequence: number; expiresAt: number }
>();

export function resetShortlistShareDedupForTests(): void {
  shareActionByShortlist.clear();
}

/** Record an operator-initiated native SMS handoff for a persistent shortlist. */
export async function recordClientShortlistShare(
  shortlistId: string,
  metadata?: { template_prefilled?: boolean }
): Promise<ClientShortlistEvent> {
  const now = Date.now();
  const prior = shareActionByShortlist.get(shortlistId);
  const action =
    prior && now < prior.expiresAt
      ? { ...prior, expiresAt: now + SHARE_ACTION_REUSE_MS }
      : {
          key: crypto.randomUUID(),
          sequence: (prior?.sequence ?? 0) + 1,
          expiresAt: now + SHARE_ACTION_REUSE_MS,
        };
  shareActionByShortlist.set(shortlistId, action);
  return api.post<ClientShortlistEvent>(`/shortlists/${shortlistId}/events`, {
    event_type: "share",
    channel: "sms",
    idempotency_key: action.key,
    client_sequence: action.sequence,
    metadata: {
      template_prefilled: Boolean(metadata?.template_prefilled),
    },
  });
}
